import { clamp01 } from './scalar.js';

export interface Hsv {
  /** Hue in degrees, [0, 360). */
  h: number;
  /** Saturation, [0, 1]. */
  s: number;
  /** Value, [0, 1]. */
  v: number;
}

export interface Oklch {
  /** Perceptual lightness, [0, 1]. */
  l: number;
  /** Chroma, >= 0 (sRGB stays under ~0.37). */
  c: number;
  /** Hue in degrees, [0, 360). */
  h: number;
}

export type Rgb = [number, number, number];

export function toHexChannel(value: number): string {
  return Math.round(clamp01(value) * 255).toString(16).padStart(2, '0');
}

export function rgbToHex(red: number, green: number, blue: number): string {
  return `#${toHexChannel(red)}${toHexChannel(green)}${toHexChannel(blue)}`;
}

export function hexToRgb(value: string): Rgb {
  const hex = value.replace('#', '');
  if (hex.length !== 6) return [0, 0, 0];
  return [
    parseInt(hex.slice(0, 2), 16) / 255,
    parseInt(hex.slice(2, 4), 16) / 255,
    parseInt(hex.slice(4, 6), 16) / 255,
  ];
}

export function normalizeHex(value: string): string | null {
  const trimmed = value.trim();
  const body = trimmed.replace(/^#+/, '').replace(/[^\da-fA-F]/g, '').slice(0, 6);
  return body.length === 6 ? `#${body.toUpperCase()}` : null;
}

export function sanitizeHexInput(value: string): string {
  const body = value.trim().replace(/^#+/, '').replace(/[^\da-fA-F]/g, '').slice(0, 6).toUpperCase();
  return `#${body}`;
}

export function previewHex(value: string): string | null {
  const body = sanitizeHexInput(value).slice(1);
  if (!body) return null;
  return `#${body.padEnd(6, '0')}`;
}

// --- sRGB <-> linear transfer ---------------------------------------------

export function srgbToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

export function linearToSrgb(c: number): number {
  return c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055;
}

// --- HSV <-> sRGB ----------------------------------------------------------
// HSV is computed on gamma-encoded sRGB, matching the familiar picker square.

export function rgbToHsv([r, g, b]: Rgb): Hsv {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0;
  if (delta > 0) {
    if (max === r) h = ((g - b) / delta) % 6;
    else if (max === g) h = (b - r) / delta + 2;
    else h = (r - g) / delta + 4;
    h *= 60;
    if (h < 0) h += 360;
  }

  const s = max === 0 ? 0 : delta / max;
  return { h, s, v: max };
}

export function hsvToRgb({ h, s, v }: Hsv): Rgb {
  const hue = ((h % 360) + 360) % 360;
  const c = v * s;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = v - c;

  let r = 0;
  let g = 0;
  let b = 0;
  if (hue < 60) [r, g, b] = [c, x, 0];
  else if (hue < 120) [r, g, b] = [x, c, 0];
  else if (hue < 180) [r, g, b] = [0, c, x];
  else if (hue < 240) [r, g, b] = [0, x, c];
  else if (hue < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];

  return [r + m, g + m, b + m];
}

// --- OKLab / OKLCH <-> sRGB ------------------------------------------------
// Björn Ottosson's OKLab; input/output sRGB channels are gamma-encoded [0, 1].

export function rgbToOklch([r, g, b]: Rgb): Oklch {
  const lr = srgbToLinear(r);
  const lg = srgbToLinear(g);
  const lb = srgbToLinear(b);

  const l = 0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb;
  const m = 0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb;
  const s = 0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb;

  const l_ = Math.cbrt(l);
  const m_ = Math.cbrt(m);
  const s_ = Math.cbrt(s);

  const okL = 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_;
  const okA = 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_;
  const okB = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_;

  const chroma = Math.hypot(okA, okB);
  let hue = (Math.atan2(okB, okA) * 180) / Math.PI;
  if (hue < 0) hue += 360;
  return { l: okL, c: chroma, h: hue };
}

/**
 * Convert OKLCH to sRGB. Components outside the sRGB gamut are clipped per
 * channel into [0, 1]; pass `clipped` to learn whether clipping occurred.
 */
export function oklchToRgb({ l, c, h }: Oklch, clipped?: { value: boolean }): Rgb {
  const hr = (h * Math.PI) / 180;
  const okA = c * Math.cos(hr);
  const okB = c * Math.sin(hr);

  const l_ = l + 0.3963377774 * okA + 0.2158037573 * okB;
  const m_ = l - 0.1055613458 * okA - 0.0638541728 * okB;
  const s_ = l - 0.0894841775 * okA - 1.291485548 * okB;

  const lc = l_ * l_ * l_;
  const mc = m_ * m_ * m_;
  const sc = s_ * s_ * s_;

  const lr = 4.0767416621 * lc - 3.3077115913 * mc + 0.2309699292 * sc;
  const lg = -1.2684380046 * lc + 2.6097574011 * mc - 0.3413193965 * sc;
  const lb = -0.0041960863 * lc - 0.7034186147 * mc + 1.707614701 * sc;

  const out: Rgb = [linearToSrgb(lr), linearToSrgb(lg), linearToSrgb(lb)];
  if (clipped) clipped.value = out.some(v => v < -1e-4 || v > 1 + 1e-4);
  return [clamp01(out[0]), clamp01(out[1]), clamp01(out[2])];
}

export function isOklchInSrgbGamut(value: Oklch): boolean {
  const clipped = { value: false };
  oklchToRgb(value, clipped);
  return !clipped.value;
}

export function maxOklchChromaInSrgb(l: number, h: number, limit = 0.34): number {
  const lightness = clamp01(l);
  const chromaLimit = Math.max(0, limit);
  if (lightness <= 1e-6 || lightness >= 1 - 1e-6 || chromaLimit <= 0) return 0;

  let lo = 0;
  let hi = chromaLimit;
  for (let i = 0; i < 18; i++) {
    const mid = (lo + hi) * 0.5;
    if (isOklchInSrgbGamut({ l: lightness, c: mid, h })) lo = mid;
    else hi = mid;
  }
  return lo;
}
